/**
 * Trigger a browser download for an in-memory blob.
 *
 * Written defensively because the naive version (create anchor → click → revoke)
 * fails silently on mobile:
 *  - Firefox ignores a click on an anchor that is not in the document;
 *  - Safari/iOS aborts the transfer if the object URL is revoked synchronously
 *    right after the click;
 *  - older iOS Safari has no `download` attribute at all — there the blob is
 *    opened instead, so the user can save it from the viewer.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  // Keep the URL alive long enough for the transfer to start, then release it.
  const revokeLater = () => setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);

  const supportsDownload =
    typeof HTMLAnchorElement !== 'undefined' && 'download' in HTMLAnchorElement.prototype;

  if (!supportsDownload) {
    window.location.href = objectUrl;
    revokeLater();
    return;
  }

  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  revokeLater();
}
